/**
 * The FlowIQ orb as a live WebGL shader (22 Sep 2026) — the way ElevenLabs
 * draws theirs, minus three.js. Replaces the Higgsfield video clip that was
 * the orb launcher for a day: a baked clip is soft at 56px, its alpha edge is
 * a blur, and it has to loop; a shader is computed every frame, so it is
 * crisp at any size and pixel density, never loops, and needs no download.
 *
 * Look: a sphere in the brand PNG's own colours (sampled from public/orb.png:
 * pink #FF6C78, blue #ABCBFF, cream #FDEEE1, peach #FF8F62, rose #FFB6AC).
 * Nine soft colour regions cover the whole sphere and ROLL about its
 * horizontal axis, top over bottom like the clip did (one turn ≈ 16 s),
 * each drifting a little so it never reads as mechanical. Soft top-left light, a faint darker rim, and the PNG's
 * outer halo as a translucent fringe. Output is premultiplied alpha on a
 * transparent canvas, so it sits on any page background.
 *
 * Dependency-free on purpose: the loader (src/loader.ts, an 12 KB IIFE) and
 * the main bundle (ChatWidget's toggle via OrbCanvas.tsx) share this one
 * file, and the phase clock is shared through `window.__flowiqOrbT0` so the
 * orb does not jump when the bundle takes over from the loader.
 *
 * Returns a destroy function, or null when WebGL is unavailable — the caller
 * then shows the still poster PNG instead. A canvas removed from the document
 * tears itself down on its next frame, so the loader's launcher swap needs no
 * bookkeeping.
 */

const VERT = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uT;

const vec3 PINK  = vec3(1.000, 0.424, 0.471);
const vec3 BLUE  = vec3(0.671, 0.796, 1.000);
const vec3 CREAM = vec3(0.992, 0.933, 0.882);
const vec3 PEACH = vec3(1.000, 0.561, 0.384);
const vec3 ROSE  = vec3(1.000, 0.714, 0.675);
const vec3 WHITE = vec3(1.000, 0.965, 0.915);

// Rotate about the horizontal (x) axis: the roll direction of the orb.
vec3 rollX(vec3 v, float a) {
  float c = cos(a), s = sin(a);
  return vec3(v.x, c * v.y - s * v.z, s * v.y + c * v.z);
}

// A colour blob on the sphere: how much of it this surface point sees.
float blob(vec3 n, vec3 dir, float spread) {
  float d = max(dot(n, dir), 0.0);
  return pow(d, spread);
}

void main() {
  float px = 1.0 / min(uRes.x, uRes.y);            // one device pixel, in uv units
  vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) * 2.0 * px;
  const float R = 0.90;                             // sphere radius; the rest is halo
  float r = length(uv) / R;

  // Surface normal on the visible hemisphere (clamped at the rim beyond it).
  float rr = min(r, 1.0);
  vec2 uvn = uv / R;
  vec3 n = normalize(vec3(uvn, sqrt(max(0.0, 1.0 - rr * rr))));

  // Colour regions on the WHOLE sphere — the front five placed like the
  // brand PNG (pink left, blue top, white glint right, peach bottom-right,
  // rose bottom-left) and four more round the back — so the orb can ROLL
  // continuously about its horizontal axis, top over bottom like the clip
  // did (one turn ≈ 16 s), and every phase still shows a vivid mix. Each
  // region also drifts on its own slow orbit so it never reads as a loop.
  float roll = uT * 0.40;
  vec3 d1 = rollX(normalize(vec3(-0.80,  0.05,  0.60)), roll);   // pink
  vec3 d2 = rollX(normalize(vec3( 0.00,  0.62,  0.78)), roll);   // blue
  vec3 d3 = rollX(normalize(vec3( 0.55, -0.70,  0.45)), roll);   // peach
  vec3 d4 = rollX(normalize(vec3( 0.85,  0.15,  0.50)), roll);   // white glint
  vec3 d5 = rollX(normalize(vec3(-0.40, -0.65,  0.65)), roll);   // rose
  vec3 d6 = rollX(normalize(vec3(-0.55,  0.35, -0.75)), roll);   // peach, back
  vec3 d7 = rollX(normalize(vec3( 0.45, -0.25, -0.85)), roll);   // blue, back
  vec3 d8 = rollX(normalize(vec3( 0.70,  0.55, -0.45)), roll);   // pink, back
  vec3 d9 = rollX(normalize(vec3(-0.30, -0.75, -0.60)), roll);   // white, back
  d1 = normalize(d1 + 0.16 * vec3(sin(uT * 0.31), cos(uT * 0.23), 0.0));
  d2 = normalize(d2 + 0.16 * vec3(cos(uT * 0.27), sin(uT * 0.19), 0.0));
  d3 = normalize(d3 + 0.16 * vec3(sin(uT * 0.22 + 1.0), cos(uT * 0.29), 0.0));
  d4 = normalize(d4 + 0.10 * vec3(cos(uT * 0.21 + 2.0), sin(uT * 0.26), 0.0));
  d5 = normalize(d5 + 0.14 * vec3(cos(uT * 0.17), sin(uT * 0.33), 0.0));
  d6 = normalize(d6 + 0.14 * vec3(sin(uT * 0.25 + 2.0), cos(uT * 0.21), 0.0));
  d7 = normalize(d7 + 0.14 * vec3(cos(uT * 0.24 + 1.0), sin(uT * 0.28), 0.0));
  d8 = normalize(d8 + 0.14 * vec3(sin(uT * 0.20 + 3.0), cos(uT * 0.30), 0.0));

  // Layered: each colour paints over the cream, sized so neighbours only
  // meet at soft boundaries and the cream shows between them.
  float wPink  = min(1.0, max(blob(n, d1, 2.4) * 1.25, blob(n, d8, 2.4) * 1.15));
  float wBlue  = min(1.0, max(blob(n, d2, 2.0) * 1.15, blob(n, d7, 2.2) * 1.10));
  float wPeach = min(1.0, max(blob(n, d3, 2.6) * 1.05, blob(n, d6, 2.6) * 1.05));
  float wWhite = min(1.0, max(blob(n, d4, 3.5) * 1.35, blob(n, d9, 3.5) * 1.25));
  float wRose  = min(1.0, blob(n, d5, 3.0) * 0.70);
  vec3 col = CREAM;
  col = mix(col, BLUE,  wBlue);
  col = mix(col, ROSE,  wRose);
  col = mix(col, PINK,  wPink);
  col = mix(col, PEACH, wPeach);
  col = mix(col, WHITE, wWhite);

  // Soft light from the top-left; the rim keeps its colour, only a touch
  // deeper, so nothing goes brown or grey at the edge.
  float lit = 0.94 + 0.10 * dot(n, normalize(vec3(-0.45, 0.65, 0.62)));
  col *= lit;
  col = mix(col, col * vec3(0.97, 0.94, 0.94), smoothstep(0.85, 1.0, rr));

  // Antialiased disc edge, then the PNG's faint translucent halo outside it.
  float aa = 1.5 * px / R;
  float disc = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, r);
  float halo = (1.0 - smoothstep(1.0, 1.08, r)) * (1.0 - disc) * 0.16;
  vec3 haloCol = col;
  float a = disc + halo;
  vec3 rgb = col * disc + haloCol * halo;

  gl_FragColor = vec4(rgb, a);                       // premultiplied
}
`;

declare global {
  interface Window {
    __flowiqOrbT0?: number;
  }
}

export interface OrbOptions {
  /** Time multiplier; 1 = the default pace. */
  speed?: number;
  /** Cap the device pixel ratio (a 56px button does not need 3x). */
  maxDpr?: number;
}

/** CSS for the canvas (or the poster it falls back to) inside the launcher. */
export const ORB_CANVAS_STYLE =
  "display:block;width:100%;height:100%;pointer-events:none;background:transparent;";

const compile = (gl: WebGLRenderingContext, type: number, src: string) => {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
};

/**
 * Start drawing the orb on `canvas`. Returns a destroy function, or null
 * when WebGL is not available (the caller shows the poster instead).
 */
export function mountOrb(canvas: HTMLCanvasElement, opts: OrbOptions = {}): (() => void) | null {
  const speed = opts.speed ?? 1;
  const maxDpr = opts.maxDpr ?? 2;
  const attrs: WebGLContextAttributes = {
    alpha: true,
    premultipliedAlpha: true,
    antialias: true,
    preserveDrawingBuffer: false,
  };
  let gl: WebGLRenderingContext | null = null;
  try {
    gl =
      (canvas.getContext("webgl", attrs) as WebGLRenderingContext | null) ||
      (canvas.getContext("experimental-webgl", attrs) as WebGLRenderingContext | null);
  } catch {
    gl = null;
  }
  if (!gl) return null;

  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  const prog = gl.createProgram();
  if (!vs || !fs || !prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);

  // One triangle that covers the clip space; the shader does the rest.
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const uRes = gl.getUniformLocation(prog, "uRes");
  const uT = gl.getUniformLocation(prog, "uT");
  gl.clearColor(0, 0, 0, 0);

  // Shared phase clock: the loader's launcher and the bundle's toggle draw
  // the same frame at the same moment, so the swap between them is invisible.
  const w = window;
  if (typeof w.__flowiqOrbT0 !== "number") w.__flowiqOrbT0 = performance.now();
  const t0 = w.__flowiqOrbT0;

  let raf = 0;
  let alive = true;
  let lastW = 0;
  let lastH = 0;

  const size = () => {
    const dpr = Math.min(w.devicePixelRatio || 1, maxDpr);
    const cw = canvas.clientWidth || canvas.width / dpr || 56;
    const ch = canvas.clientHeight || canvas.height / dpr || 56;
    const pw = Math.max(1, Math.round(cw * dpr));
    const ph = Math.max(1, Math.round(ch * dpr));
    if (pw !== lastW || ph !== lastH) {
      canvas.width = pw;
      canvas.height = ph;
      lastW = pw;
      lastH = ph;
      gl!.viewport(0, 0, pw, ph);
    }
  };

  const draw = () => {
    size();
    gl!.clear(gl!.COLOR_BUFFER_BIT);
    gl!.uniform2f(uRes, lastW, lastH);
    gl!.uniform1f(uT, ((performance.now() - t0) / 1000) * speed);
    gl!.drawArrays(gl!.TRIANGLES, 0, 3);
  };

  const frame = () => {
    if (!alive) return;
    if (!canvas.isConnected) {
      destroy();
      return;
    }
    draw();
    raf = requestAnimationFrame(frame);
  };

  // Stop drawing while the tab is hidden; pick the phase back up on return.
  const onVisibility = () => {
    if (!alive) return;
    if (document.hidden) {
      cancelAnimationFrame(raf);
      raf = 0;
    } else if (!raf) {
      raf = requestAnimationFrame(frame);
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  const destroy = () => {
    if (!alive) return;
    alive = false;
    cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", onVisibility);
    try {
      gl!.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      /* already gone */
    }
  };

  // First frame synchronously: a background tab (where rAF never fires)
  // still holds a finished orb the moment it is brought forward.
  draw();
  raf = requestAnimationFrame(frame);
  return destroy;
}
